import { Button, ConfigProvider, Drawer, InputNumber } from 'antd';
import { ShoppingBag, Minus, Plus, X } from 'lucide-react';
import { useTranslation } from "react-i18next";
import { useCart } from "../../context/UserCartContext.jsx";
import useFetchProducts from "../../hooks/useFetchProducts.js";
import { useNavigate } from 'react-router-dom';
import useFetchBasketRecommendations from "../../hooks/useFetchBasketRecommendations.js";
import BasketRecommendation from "../Card/BasketRecommendation.jsx";
import './styles.css';

const UserCart = () => {
    const { drawerVisible, setDrawerVisible } = useCart();
    const { cart, removeFromCart, updateCartQty } = useCart();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { products } = useFetchProducts();
    const { basketRecommendations } = useFetchBasketRecommendations();

    const toggleDrawer = () => {
        setDrawerVisible(!drawerVisible);
    };

    const getProductById = (id) => products.find(product => product.id === id) || null;

    const handleIncrease = (item) => {
        const product = getProductById(item.id);
        if (product && item.qty < product.qty) {
            updateCartQty(item.id, item.qty + 1, product.qty);
        }
    };

    const handleDecrease = (item) => {
        const product = getProductById(item.id);
        if (product && item.qty > 1) {
            updateCartQty(item.id, item.qty - 1, product.qty);
        }
    };

    const getTotalPrice = () => cart.reduce((total, item) => {
        const product = getProductById(item.id);
        return total + (product ? product.price * item.qty : 0);
    }, 0);

    return (
        <div>
            <Button
                type="link"
                size="middle"
                icon={<ShoppingBag color="black" size={32} strokeWidth={1} />}
                onClick={toggleDrawer}
            />
            <Drawer
                placement="right"
                onClose={toggleDrawer}
                open={drawerVisible}
                closeIcon={null}
                width={382}
                rootStyle={{ top: 64 }}
            >
                <div className="flex flex-col">
                    <h2 className="text-xl font-bold mt-5">
                        {cart.length === 0 ? t("emptyCart") : `${t("cart", { count: cart.length })}`}
                    </h2>

                    <div className={`${cart.length > 0 ? 'h-full' : ''} overflow-y-auto`}>
                        {cart.map((item) => {
                            const product = getProductById(item.id);
                            const totalItemPrice = product ? product.price * item.qty : 0;

                            return (
                                <div key={item.id} className="flex justify-between items-center py-3 border-b border-gray-100">
                                    <div className="flex items-center gap-3 w-[260px]">
                                        <img
                                            src={item.image}
                                            alt={item.name}
                                            className="w-12 h-12 object-contain rounded-lg bg-white flex-shrink-0"
                                        />
                                        <div className="flex flex-col justify-center">
                                            <p className="text-sm font-medium leading-tight break-words whitespace-normal">
                                                {item.name}
                                            </p>
                                            <span className="text-gray-500 text-xs">
                                                {item.qty > 1 ? `x${item.qty} / ${totalItemPrice} ₽` : `x1 / ${totalItemPrice} ₽`}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex items-center space-x-1">
                                        <Button
                                            type="text"
                                            size="small"
                                            onClick={() => handleDecrease(item)}
                                            style={{ width: 32, height: 32, padding: 0 }}
                                        >
                                            <Minus size={18} />
                                        </Button>

                                        <ConfigProvider
                                            theme={{
                                                components: {
                                                    InputNumber: { controlWidth: 44 },
                                                },
                                                token: { controlHeightSM: 32 }
                                            }}
                                        >
                                            <InputNumber
                                                min={1}
                                                size="small"
                                                controls={false}
                                                max={product?.qty || 99}
                                                value={item.qty}
                                                onChange={(value) => {
                                                    const intVal = parseInt(value, 10);
                                                    if (!isNaN(intVal) && intVal >= 1 && intVal <= (product?.qty || 99)) {
                                                        updateCartQty(item.id, intVal, product?.qty || 99);
                                                    }
                                                }}
                                                className="centered-input"
                                            />
                                        </ConfigProvider>

                                        <Button
                                            type="text"
                                            size="small"
                                            onClick={() => handleIncrease(item)}
                                            disabled={product && item.qty >= product.qty}
                                            style={{ width: 32, height: 32, padding: 0 }}
                                        >
                                            <Plus size={18} />
                                        </Button>

                                        <Button
                                            type="text"
                                            size="small"
                                            onClick={() => removeFromCart(item.id)}
                                            style={{ width: 32, height: 32, padding: 0 }}
                                        >
                                            <X size={16} />
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {basketRecommendations?.products?.length > 0 && (
                        <div className="mt-5 mb-5 border border-gray-200 rounded-2xl p-4">
                            <BasketRecommendation
                                basketRecommendations={basketRecommendations}
                                products={products}
                            />
                        </div>
                    )}

                    {cart.length > 0 && (
                        <div className="mt-auto">
                            <div className="flex justify-between items-center mb-4">
                                <span className="font-normal text-lg">{t("total")}</span>
                                <span className="font-semibold text-2xl">{getTotalPrice()} ₽</span>
                            </div>
                            <Button
                                size="large"
                                type="primary"
                                onClick={() => {
                                    setDrawerVisible(false);
                                    navigate("/order");
                                }}
                                className="w-full"
                                style={{
                                    backgroundColor: "#0da98c",
                                    borderColor: "#0da98c",
                                    color: "white",
                                    borderRadius: "0px",
                                    fontSize: "0.875rem",
                                    fontWeight: "500"
                                }}
                            >
                                {t("checkout") || "Оформить заказ"}
                            </Button>
                        </div>
                    )}
                </div>
            </Drawer>
        </div>
    );
};

export default UserCart;